/**
 * Die Kontext-Sidebar — die zweite Spalte der Hülle (ADR 0011).
 *
 * Sie **wechselt mit dem Bereich**: Auf *Objekte* trägt sie den Objektbaum,
 * sonst die Ansichten des jeweiligen Bereichs. Das ist der Unterschied zum
 * Zustand davor, in dem an dieser Stelle immer der Objektbaum stand — auch auf
 * *Heute* und auf *Geld*, wo er keine Frage der Ansicht beantwortet. Der Baum
 * sagt zuverlässig, was in einem Gebäude steckt; er sagt nichts über alle
 * überfälligen Forderungen hinweg.
 *
 * **Rein darstellend.** `components/` darf `features/` nicht importieren
 * (eslint-boundaries), der Inhalt kommt aber von dort. Er ist deshalb ein
 * Slot, verdrahtet in `router.tsx` — dieselbe Aufteilung wie bei `Rail`.
 *
 * **Keine Suchzeile**, obwohl der Entwurf B eine zeigt. Der `design-system`-
 * Skill sieht dafür `Cmd+K` vor; solange die Befehlspalette fehlt, ist
 * *Suchen* ein eigener Eintrag in der Symbolleiste (`rail-suchen`,
 * `apps/web/CLAUDE.md`). Eine zweite Suchzeile daneben wäre ein doppeltes
 * Ziel für dieselbe Sache — und ein `⌘K`-Kürzel anzuzeigen, das nichts
 * auslöst, ist ein Versprechen, das die Oberfläche nicht hält. Sie entsteht
 * mit der Befehlspalette.
 *
 * Schreibtisch-only: Unter 768px gibt es keine Sidebar, dort navigiert die
 * Tab-Leiste in `AppShell`. Gemountet wird sie deshalb gar nicht erst
 * (`useIsDesktopViewport` in `router.tsx`) — nicht nur per CSS versteckt,
 * sonst stünden `property-add` und jede `*-row-<id>` doppelt im Dokument
 * (Spec 01, „Der Baum").
 */

import type { ReactNode } from 'react'

interface ContextSidebarProps {
  /**
   * Der Name des Bereichs, in dem man steht. Er wiederholt die Beschriftung
   * aus der Symbolleiste bewusst: Dort trägt sie ein Symbol und steht klein
   * darunter; hier ist sie die Überschrift über dem, was zum Bereich gehört.
   */
  readonly title: string
  readonly children: ReactNode
}

export function ContextSidebar({ title, children }: ContextSidebarProps) {
  return (
    <div
      data-testid="sidebar"
      // `print:hidden` — die Sidebar gehört zur Hülle, nicht zum druckbaren
      // Nachweis des Mietkontos (Spec 04c, „Drucken", AK-335). Der Baum darin
      // verschwindet damit mit, ohne selbst eine Druckregel zu brauchen.
      className="border-border bg-surface flex w-(--sidebar-w) shrink-0 flex-col border-r print:hidden"
    >
      <header className="border-border flex h-(--row-h) shrink-0 items-center border-b px-(--sidebar-x)">
        <h2 data-testid="sidebar-title" className="text-section font-(--font-weight-section) truncate">
          {title}
        </h2>
      </header>

      {/* `min-h-0`, sonst wächst der Inhalt über die Spalte hinaus, statt in
       * ihr zu scrollen — die bekannte Falle bei `flex-1` in einer Spalte mit
       * fester Höhe. */}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  )
}
